import { Button } from '@leadsales/design-system/react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import FormDialog from 'src/components/FormDialog/FormDialog.component';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import LeadCloseOptionsEditor from './LeadCloseOptionsEditor.component';
import { useLeadCloseOptionsDraft } from './useLeadCloseOptionsDraft.hook';

/**
 * "Editar opciones" from the result form (U17): opens over the form, so what
 * the seller typed stays; Escape closes only this dialog (FormDialog stack).
 * A colleague's change (settings_conflict) reloads the lists and keeps it open.
 */
export default function LeadCloseOptionsDialog(props: { onClose: () => void }) {
  const { onClose } = props;
  const { t } = useTranslation('chat/chat');
  const { addAlert } = useToastContext();
  const {
    draft,
    change,
    error,
    notice,
    busy,
    save: saveDraft,
  } = useLeadCloseOptionsDraft();

  const save = async () => {
    const outcome = await saveDraft();
    if (outcome === 'refused') return;
    if (outcome === 'saved')
      addAlert({
        type: eAlertType.SUCCESS,
        title: t('leadClose.settings.dialog.saved'),
        message: '',
      });
    onClose();
  };

  return createPortal(
    <FormDialog
      id="lead-close-options"
      title={t('leadClose.settings.dialog.title')}
      description={t('leadClose.settings.dialog.sub')}
      busy={busy}
      closeLabel={t('leadClose.form.close')}
      onClose={onClose}
      onSubmit={() => void save()}
      actions={
        <>
          <Button type="button" color="ghost" disabled={busy} onClick={onClose}>
            {t('leadClose.form.cancel')}
          </Button>
          <Button type="submit" color="primary" loading={busy}>
            {t('leadClose.settings.dialog.save')}
          </Button>
        </>
      }
    >
      {draft ? (
        <LeadCloseOptionsEditor
          draft={draft}
          onChange={change}
          prefix="lead-close-options"
          error={error}
          disabled={busy}
        />
      ) : null}
      {notice ? (
        <p className="FormDialog__Error" role="alert">
          {notice}
        </p>
      ) : null}
    </FormDialog>,
    document.body,
  );
}
